import { ErrorMessage } from '@hookform/error-message';
import { useGetClientProfile } from 'admin/features/clients/viewClientDetails/accountInfoClientTab';
import { useGetSpecializationsQuery } from 'app/services/accountant';
import {
  useUpdateMyProfileMutation,
  useUpdateMyProfilePictureMutation,
  useUpdateUserProfileMutation,
  useUpdateUserProfilePictureMutation,
} from 'app/services/user';
import { EditPictureIcon, EmailIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import { SelectInput } from 'Components/Auth/SelectInput';
import { TextInput } from 'Components/Auth/TextInput';
import Form from 'Components/Form';
import ErrorMessageStyle from 'Components/Form/ErrorMessageStyle';
import { successToast } from 'Components/Notifications';
import UserInfo from 'Components/Profile/UserInfo';
import LanguageSelector from 'Components/Selectors/LanguageSelector';
import TimezoneSelector from 'Components/Selectors/TimezoneSelector';
import Spinner from 'Components/Spinner';
import UserAvatar from 'Components/UserAvatar';
import { countries } from 'countries';
import { Controller, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { states } from 'states';
import { useAuth } from 'utils/AuthProvider';
import { FormInputs, serviceTypeOptions, validationSchema } from './formFields';
import PhoneNumberEditor from './PhoneNumberEditor';

export function useUpdateClient() {
  const { client, clientId } = useGetClientProfile();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [UpdateUserProfile, { isLoading: isUpdatingUser }] =
    useUpdateUserProfileMutation();
  const [UpdateMyProfile, { isLoading: isUpdatingMe }] =
    useUpdateMyProfileMutation();

  const UpdateClient: SubmitHandler<FormInputs> = async (data) => {
    if (!client || !clientId) return;

    const { is_verified_phone_number, ...updates } = data;
    return (
      clientId === userId
        ? UpdateMyProfile({ user: updates })
        : UpdateUserProfile({
            user: updates,
            userId: clientId,
          })
    ).unwrap();
  };

  return { client, UpdateClient, isLoading: isUpdatingUser || isUpdatingMe };
}

function useUpdateAccount() {
  const { UpdateClient, ...rest } = useUpdateClient();

  const navigate = useNavigate();

  const UpdateAccount: SubmitHandler<FormInputs> = async (data) => {
    UpdateClient(data).then(() => {
      successToast('Profile Updated Successfully');
      navigate('..');
    });
  };

  return { UpdateAccount, ...rest };
}

function useUpdateProfilePicture() {
  const { client, clientId } = useGetClientProfile();
  const { userId } = useAuth();

  const [UpdateUserProfilePicture, { isLoading: l1 }] =
    useUpdateUserProfilePictureMutation();
  const [UpdateMyProfilePicture, { isLoading: l2 }] =
    useUpdateMyProfilePictureMutation();
  const UpdateProfilePicture = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!client || !clientId) return;

    e.stopPropagation();
    if (e.target.files && e.target.files[0]) {
      let form = new FormData();
      form.append('user[picture]', e.target.files[0]);
      (clientId === userId
        ? UpdateMyProfilePicture({ user: form })
        : UpdateUserProfilePicture({
            user: form,
            userId: clientId,
          })
      ).unwrap();
    }
  };
  return {
    UpdateProfilePicture,
    client,
    isLoading: l1 || l2,
  };
}

function EditProfilePitcureButton() {
  const { UpdateProfilePicture, isLoading, client } = useUpdateProfilePicture();

  return (
    <div className="grid grid-cols-12 gap-2 py-4 text-xs rounded-xl w-full">
      <label className="cursor-pointer  relative h-10 w-10  flex justify-center items-center">
        <input
          hidden
          type="file"
          accept=" .png,  .jpeg"
          disabled={isLoading}
          onChange={UpdateProfilePicture}
        />
        {isLoading && <Spinner bounce={false} />}
        {!isLoading && (
          <UserAvatar user={client} size="w-10 h-10 md-max:h-10 md-max:w-10" />
        )}

        <div className="absolute -bottom-1 -right-5 ">
          <EditPictureIcon />
        </div>
      </label>

      <div className="col-span-10 col-start-3  lg-max:col-start-4">
        <p className="text-xs text-gray-700 mt-3">Change Profile Picture</p>
      </div>
    </div>
  );
}

export default function EditAccountInfo() {
  const { client, UpdateAccount } = useUpdateAccount();

  const { data: specializations } = useGetSpecializationsQuery();

  let navigate = useNavigate();

  if (!client) return <Spinner />;

  let initialData: FormInputs = {
    given_name: client?.given_name || '',
    family_name: client?.family_name || '',
    email: client?.email || '',
    zipcode: client?.zipcode || '',
    state: client?.state || '',
    country: client?.country || '',
    city: client?.city || '',
    address: client?.address || '',
    service_type: client?.service_type || '',
    time_zone_id: client?.time_zone?.id,
    language_id: client?.language?.id,
    specialization_id: client?.specialization?.id,
    is_verified_phone_number: true,
  };
  return (
    <div className="p-4">
      <div>
        <p className="font-bold  text-medium">Edit Profile</p>
        <hr className="mt-7" />
      </div>
      <div className="mt-6">
        <p className="text-green text-base font-bold ">
          Your Contact Information
        </p>
        <div className="mb-6 mt-2 ">
          <Form
            onSubmit={UpdateAccount}
            defaultValues={initialData}
            validationSchema={validationSchema(initialData)}
          >
            {({ control, formState }) => (
              <>
                <div className="w-1/3 mb-3 md-max:w-full">
                  <EditProfilePitcureButton />
                </div>
                <div className="grid grid-cols-2 md-max:grid-cols-1  w-2/3 md-max:w-full gap-x-3 gap-y-3">
                  <div className="w-full ">
                    <UserInfo
                      userInfo={client?.email || 'N/A'}
                      label="Email Address"
                      icon={<EmailIcon />}
                    />
                  </div>
                  <div className="w-full">
                    <Controller
                      name="is_verified_phone_number"
                      render={({ field }) => (
                        <>
                          <PhoneNumberEditor
                            phoneNumber={client.phone_number}
                            onPhoneNumberChange={(phone_number) => {
                              field.onChange(
                                phone_number === client.phone_number,
                              );
                            }}
                            onPhoneNumberVerify={() => field.onChange(true)}
                          />

                          <ErrorMessage
                            name="is_verified_phone_number"
                            errors={formState.errors}
                            render={({ message }) => (
                              <ErrorMessageStyle message={message} />
                            )}
                          />
                        </>
                      )}
                    ></Controller>
                  </div>
                  <div className="w-full">
                    <TextInput
                      name="given_name"
                      label="First Name"
                      placeholder="First Name"
                      type="text"
                    />
                  </div>

                  <div className="w-full">
                    <TextInput
                      name="family_name"
                      label="Last Name"
                      placeholder="Last Name"
                      type="text"
                    />
                  </div>

                  <div className="col-span-2 md-max:col-span-1">
                    <SelectInput
                      name="service_type"
                      label="Service Type"
                      options={serviceTypeOptions}
                      type="select"
                      placeholder="Select Service Type"
                    />
                  </div>
                  <div className="col-span-2 md-max:col-span-1">
                    <SelectInput
                      name="specialization_id"
                      label="Your Industry"
                      options={specializations || []}
                      type="select"
                      placeholder="Select Industry"
                    />
                  </div>

                  <div className="col-span-2 md-max:col-span-1 mt-4 text-green text-base font-bold ">
                    Your Location
                  </div>

                  <SelectInput
                    name="country"
                    label="Country"
                    options={countries}
                    type="select"
                    placeholder="Select Country"
                  />

                  <div className="w-full">
                    <LanguageSelector />
                  </div>
                  <div className="w-full col-span-2 md-max:col-span-1">
                    <TextInput
                      name="address"
                      label="Street Address"
                      placeholder="Street Address"
                      type="text"
                    />
                  </div>
                  <div className="w-full">
                    <SelectInput
                      name="state"
                      label="State"
                      options={states}
                      type="select"
                      placeholder="Select State"
                    />
                  </div>

                  <div className="w-full">
                    <TextInput
                      name="city"
                      label="City"
                      placeholder="City"
                      type="text"
                    />
                  </div>

                  <div className="w-full">
                    <TextInput
                      name="zipcode"
                      label="Zipcode"
                      placeholder="zipcode"
                      type="text"
                    />
                  </div>

                  <div className="w-full">
                    <TimezoneSelector />
                  </div>
                </div>

                <div className="w-1/3 flex flex-inline gap-4 mt-6 md-max:w-full md-max:grid">
                  <CustomButton type="submit" text="Save" height="10" />
                  <CustomButton
                    type="button"
                    text="Cancel"
                    bgColor="white"
                    textColor="green"
                    borderColor="green"
                    height="10"
                    onClick={() => navigate(`..`)}
                  />
                </div>
              </>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
}
